import { z } from 'zod';
import { TOP_LEVEL_PARENT_ENTITY_TYPES } from '../constants/parent-validity';
/**
 * Connectors platform — public types shared between backend, frontend, and
 * the agent service. The platform reads from researcher tools via a managed
 * MCP gateway (Composio); items only land in Bower's DB when the user
 * explicitly saves a federated result.
 */
export const CONNECTION_STATUSES = [
    'INITIATED',
    'ACTIVE',
    'NEEDS_REAUTH',
    'ERROR',
    'INACTIVE',
    'REVOKED',
];
/**
 * Bower-curated allowlist of toolkits researchers actually want.
 *
 * Composio's gateway exposes 100+ generic SaaS apps (Netsuite, Zoho Books,
 * etc.); the list endpoint filters down to this set so the UI only shows
 * tools that fit a research-lab workflow. Add new keys here when product
 * expands the supported set; the gateway slug must match Composio's
 * canonical lowercase slug exactly — verified against
 * `GET https://backend.composio.dev/api/v3/toolkits` for v0.8.1.
 *
 * Slug conventions (per the live catalog): lowercase, underscores only
 * between distinct words (``microsoft_teams``, ``one_drive``); a single
 * company's products are concatenated (``googledrive``, ``googledocs``,
 * ``googlesheets``, ``googlecalendar``). The docs sometimes display slugs
 * uppercase (``SLACK``) for emphasis — those are stylistic; the real
 * identifier returned by the SDK is lowercase.
 *
 * To deprecate a toolkit, remove the key — the backend stops surfacing it
 * and existing connections keep working until users disconnect them.
 */
export const SUPPORTED_TOOLKIT_KEYS = [
    'googlesuper',
    'microsoft_workspace',
    'googledrive',
    'googledocs',
    'googlesheets',
    'notion',
    'slack',
    'dropbox',
    'one_drive',
    'microsoft_teams',
    'linear',
    'airtable',
    'box',
    'asana',
    'monday',
    'onenote',
];
/**
 * O(1) membership check for the curated allowlist.
 */
const SUPPORTED_TOOLKIT_KEY_SET = new Set(SUPPORTED_TOOLKIT_KEYS);
export function isSupportedToolkit(key) {
    return SUPPORTED_TOOLKIT_KEY_SET.has(key);
}
/**
 * Human-readable label for a toolkit slug. Used in audit-log summaries
 * ("Connected Google Drive"), error messages, and any other surface
 * where the gateway-style slug is too cryptic. The mapping is
 * intentionally kept here in shared so backend and frontend agree on
 * the label without a network round trip.
 *
 * Falls back to a Title-Cased version of the slug for unknown keys —
 * keeps logs readable when a new toolkit gets added before this map.
 */
const TOOLKIT_DISPLAY_NAMES = {
    googlesuper: 'Google Workspace',
    microsoft_workspace: 'Microsoft Workspace',
    googledrive: 'Google Drive',
    googledocs: 'Google Docs',
    googlesheets: 'Google Sheets',
    notion: 'Notion',
    slack: 'Slack',
    dropbox: 'Dropbox',
    one_drive: 'OneDrive',
    microsoft_teams: 'Microsoft Teams',
    linear: 'Linear',
    airtable: 'Airtable',
    box: 'Box',
    asana: 'Asana',
    monday: 'Monday',
    onenote: 'OneNote',
};
export function toolkitDisplayName(key) {
    if (isSupportedToolkit(key)) {
        return TOOLKIT_DISPLAY_NAMES[key];
    }
    // Unknown / retired toolkit — Title Case the slug as a stable fallback.
    return key
        .split(/[_-]/)
        .map((part) => (part ? part.charAt(0).toUpperCase() + part.slice(1) : part))
        .join(' ');
}
export const SaveExternalArtifactSchema = z.strictObject({
    /**
     * Free-form connector key. Always set, even when the source isn't
     * a managed-gateway toolkit (e.g. native "openalex"). Free-form
     * because it is a component of the persisted dedupe key, so an
     * unrecognised source still has to be able to name itself.
     *
     * Never forward this value to a third party verbatim: on the MCP
     * path it is model-authored and on the REST path it is raw request
     * body. The `Connector Save To Bower` analytics dimension clamps it
     * through `backend/services/connectors/external-source-analytics.ts`.
     */
    externalSource: z.string().min(1).max(64),
    externalId: z.string().min(1).max(256),
    sourceUri: z.url().max(2048),
    /**
     * The id of the `Connection` row this came from. Optional because
     * native sources (ORCID / OpenAlex) have no `Connection` row.
     */
    savedFromConnectionId: z.guid().optional(),
    title: z.string().min(1).max(500),
    /** Markdown body — rendered into the Artifact content field. */
    markdown: z.string().max(200_000).optional(),
    /**
     * Where to land the artifact in the user's hierarchy. Mirrors the
     * existing `parentEntityType` + `parentEntityId` polymorphic pattern.
     */
    parentEntityType: z.enum(TOP_LEVEL_PARENT_ENTITY_TYPES),
    parentEntityId: z.guid(),
    /**
     * Free-form metadata captured from the source (DOI authors, year,
     * mime type, …). JSON-serialisable; stored as-is on Artifact.metadata.
     */
    metadata: z.record(z.string(), z.unknown()).optional(),
});
/**
 * SPA route where the connector OAuth round-trip lands (rendered outside
 * ProtectedRoute). SSOT for the frontend route registration
 * (`ROUTES.connectorCallback`) and the backend's Composio callback-URL
 * builder (`composio.gateway.ts`) — these were hand-mirrored comment-only
 * and a rename on either side would strand the OAuth popup on the SPA
 * catch-all route, spinning forever (#649 review, CR-003).
 */
export const CONNECTOR_OAUTH_CALLBACK_PATH = '/connectors/callback';
//# sourceMappingURL=types.js.map